/* eslint-disable no-console */
import {
  MutationActionCreatorResult,
  MutationDefinition,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { Dispatch, SetStateAction } from 'react';

import { LOCAL_STORAGE, VISITOR_SOURCE } from '../constants/local-storage';
import { store } from '../redux/configure-store';
import {
  setDeviceNativeInfo,
  setPushToken,
  initialState,
  setInstalledApps,
} from '../redux/modules/app';
import { DISPLAY_42, GGL_REF_CODE, RANDOM_DEEP_LINK } from '../utils/random-deep-link';
import { authExtendedApiSlice } from '../redux/services/auth';
import { chatApiSlice } from '../redux/services/chat';
import { PostCalcQueryType, CalculatorResponseType } from '../types/calculator';
import { getParamsDeepLinkUrl } from '../utils/get-params-deep-link-url';
import { isWebContext } from '../utils/is-web-context';

export type DeviceInfo = {
  deviceId: string;
  appVersion: string;
  system: string;
  deviceModel: string;
  osVersion: string;
  platform: string;
  deepLinkUrl: string;
  deepLink: {
    status: string;
    value: string | null;
    subs: string | null;
  };
  fingerprintVisitorId?: string;
  conversionData: {
    status: string;
    data: {
      [key: string]: string;
    };
  };
};

export enum NativeOperations {
  GET_PUSH_TOKEN = 'GET_PUSH_TOKEN',
  CHECK_TOKEN_PROTECTION = 'CHECK_TOKEN_PROTECTION',
  PROTECT_TOKEN = 'PROTECT_TOKEN',
  RETRIEVE_TOKEN = 'RETRIEVE_TOKEN',
  RETRIEVE_LOCATION = 'RETRIEVE_LOCATION',
  RETRIEVE_VISITOR_ID = 'RETRIEVE_VISITOR_ID',
  RETRIEVE_INSTALLED_APPS = 'RETRIEVE_INSTALLED_APPS',
  RETRIEVE_BLACKBOX = 'RETRIEVE_BLACKBOX',
}

type CallNativeType = {
  operation: NativeOperations;
  parameters?: any;
  callBackFoo: (p?: any) => void;
};

type SetTokenTypePayload = OnResponseType & {
  token: string;
};

type OnResponseType = {
  onSuccess?: (token?: string) => void;
  onError?: () => void;
};

type OnLocationResponse = {
  onSuccess?: (res: GeolocationPosition) => void;
  onError?: (res: string) => void;
};

enum ResStatusEnum {
  OK = 'OK',
  ERROR = 'ERROR',
  CANCELED = 'CANCELED',
}

type NativeCallResponse = {
  status: ResStatusEnum;
  token?: string;
};

export type NativeLocationResponse = {
  status: ResStatusEnum;
  latitude?: number;
  longitude?: number;
};

type NativeGetPushTokenResponse = {
  token: string;
};

type NativeGetInstalledAppsResponse = {
  status: ResStatusEnum;
  installedApps: string[];
};

export type NativeVisitorIdResponse = {
  status: ResStatusEnum;
  fingerprintVisitorId: string;
  fingerprintRequestId: string;
  deepLinkUrl: string;
};

export type NativeTrustSdk = {
  status: ResStatusEnum;
  blackbox?: string;
  deepLinkUrl?: string;
};

export const getNativeDeviceInfo = () => {
  const { versionGplayReview, versionGplayReviewCurrent } = initialState;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function handleNativeInfo(info: DeviceInfo) {
    console.log('deviceInfo', info);

    if (info.deepLinkUrl) {
      getParamsDeepLinkUrl(info.deepLinkUrl, info.appVersion);
    }

    const updateDeviceInfo = (deviceInfo: DeviceInfo) => {
      const updatedValue =
        deviceInfo?.deepLink?.value && deviceInfo.deepLink.value !== 'null'
          ? deviceInfo?.deepLink?.value
          : RANDOM_DEEP_LINK;

      // todo: new ref
      if (info.appVersion.includes('gplay')) {
        localStorage.setItem(LOCAL_STORAGE.isGgl, 'true');
      }

      if (versionGplayReview.includes(info.appVersion)) {
        if (versionGplayReviewCurrent.includes(info.appVersion)) {
          localStorage.setItem(LOCAL_STORAGE.refCode, GGL_REF_CODE);
        } else localStorage.setItem(LOCAL_STORAGE.refCode, DISPLAY_42);
        // Вот тут были вопросики: удаляли, чтоб GGL подставлялся
      } else if (!localStorage.getItem(LOCAL_STORAGE.refCode)) {
        localStorage.setItem(LOCAL_STORAGE.refCode, updatedValue);
      }

      return {
        ...deviceInfo,
        deepLink: {
          ...deviceInfo?.deepLink,
          value: updatedValue,
        },
      };
    };

    if (info) {
      store.dispatch(setDeviceNativeInfo(updateDeviceInfo(info)));
    }

    if (!localStorage.getItem(LOCAL_STORAGE.deviceId)) {
      localStorage.setItem(LOCAL_STORAGE.deviceId, info.deviceId);
    }

    if (!localStorage.getItem(LOCAL_STORAGE.platform)) {
      localStorage.setItem(LOCAL_STORAGE.platform, info.platform);
    }
    if (!localStorage.getItem(LOCAL_STORAGE.deviceModel)) {
      localStorage.setItem(LOCAL_STORAGE.deviceModel, info.deviceModel);
    }
  }
  const getNativeInfo = () => {
    const callbackName = 'handleNativeInfo';

    if (window.Android && window.Android.monetechGetNativeInfo) {
      window.Android.monetechGetNativeInfo(JSON.stringify({ callbackName }));
    }

    if (
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.monetechGetNativeInfo
    ) {
      window.webkit.messageHandlers.monetechGetNativeInfo.postMessage(
        JSON.stringify({ callbackName }),
      );
    }
  };

  (window as any).handleNativeInfo = handleNativeInfo;

  getNativeInfo();
};

const callNative = ({ operation, parameters = {}, callBackFoo }: CallNativeType) => {
  const callbackName = (callBackFoo.name || 'handleCallNative') + String(Date.now());

  (window as any)[callbackName] = function (result: any) {
    console.log('response', result);
    callBackFoo(result);

    delete (window as any)[callbackName];
  };

  console.log(
    JSON.stringify({
      operation,
      parameters,
      callbackName,
    }),
  );

  if (window.Android && window.Android.monetechCallNative) {
    window.Android.monetechCallNative(JSON.stringify({ operation, parameters, callbackName }));
  }
  if (
    window.webkit &&
    window.webkit.messageHandlers &&
    window.webkit.messageHandlers.monetechCallNative
  ) {
    window.webkit.messageHandlers.monetechCallNative.postMessage(
      JSON.stringify({
        operation,
        parameters,
        callbackName,
      }),
    );
  }
};

export const bioAuth = ({ onSuccess, onError }: OnResponseType) => {
  const handleBio = (res: NativeCallResponse) => {
    if (res.status === ResStatusEnum.OK && res?.token) {
      return store.dispatch(
        authExtendedApiSlice.endpoints?.refreshBIO.initiate({ token: res.token, onSuccess }),
      );
    }
    if (res.status === ResStatusEnum.CANCELED) {
      return onError?.();
    }
    localStorage.removeItem(LOCAL_STORAGE.bioRefreshTimer);
    localStorage.removeItem(LOCAL_STORAGE.biometrics);
    onError?.();
  };

  callNative({ operation: NativeOperations.RETRIEVE_TOKEN, callBackFoo: handleBio });
};

export const checkBioAbility = ({ onError, onSuccess }: OnResponseType) => {
  const handleCheckAbility = (res: NativeCallResponse) => {
    if (res?.status === ResStatusEnum.OK) {
      onSuccess?.();
    } else {
      onError?.();
    }
  };

  callNative({
    operation: NativeOperations.CHECK_TOKEN_PROTECTION,
    callBackFoo: handleCheckAbility,
  });
};

export const setNativeToken = ({ token, onSuccess, onError }: SetTokenTypePayload) => {
  const setToken = (res: NativeCallResponse) => {
    if (res?.status === 'OK') {
      onSuccess?.();
    } else {
      onError?.();
    }
  };

  callNative({
    operation: NativeOperations.PROTECT_TOKEN,
    parameters: { token },
    callBackFoo: setToken,
  });
};

export const getPushToken = () => {
  const getToken = (res: NativeGetPushTokenResponse) => {
    if (res?.token) {
      store.dispatch(setPushToken(res.token));
      store.dispatch(
        chatApiSlice.endpoints?.getChatData.initiate({ token: res?.token, withPushTokenReq: true }),
      );
    }
  };

  callNative({
    operation: NativeOperations.GET_PUSH_TOKEN,
    callBackFoo: getToken,
  });
};

export const getLocationMobile = ({ onError, onSuccess }: OnLocationResponse) => {
  const handleGetLocation = (res: NativeLocationResponse) => {
    if (
      res?.status === ResStatusEnum.OK &&
      res?.longitude !== undefined &&
      res?.latitude !== undefined
    ) {
      onSuccess?.({
        coords: { latitude: res.latitude, longitude: res.longitude },
      } as GeolocationPosition);
    } else {
      onError?.(res.status);
    }
  };

  callNative({
    operation: NativeOperations.RETRIEVE_LOCATION,
    callBackFoo: handleGetLocation,
  });
};

export const getVisitorId = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSuccess: {
    (arg: PostCalcQueryType): MutationActionCreatorResult<
      MutationDefinition<
        PostCalcQueryType,
        BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
        never,
        CalculatorResponseType,
        'api'
      >
    >;
    (arg0: any): void;
  },
  postData: PostCalcQueryType,
  setIsFingerprintDataLoading: Dispatch<SetStateAction<boolean>>,
) => {
  const handleVisitorId = (res: NativeVisitorIdResponse) => {
    if (res.status === ResStatusEnum.OK && res?.fingerprintVisitorId) {
      const visitorData = {
        visitorId: res?.fingerprintVisitorId,
        visitorSource: VISITOR_SOURCE.mobApp,
        requestId: res?.fingerprintRequestId,
      };

      localStorage.setItem(LOCAL_STORAGE.xFingerprintVisitorId, JSON.stringify(visitorData));

      if (visitorData) {
        // eslint-disable-next-line no-param-reassign
        postData.deviceDetails = {
          ...postData.deviceDetails,
          fingerprintVisitorId: visitorData,
        };
      }

      const urlParams = new URLSearchParams(res.deepLinkUrl);

      if (urlParams.has('fingerprint_request_id') && urlParams.has('fingerprint_visitor_id')) {
        const fingerprintRequestId = urlParams.get('fingerprint_request_id');
        const fingerprintVisitorId = urlParams.get('fingerprint_visitor_id');

        const visitorLandingData = {
          visitorId: fingerprintVisitorId,
          visitorSource: VISITOR_SOURCE.landing,
          requestId: fingerprintRequestId,
        };

        localStorage.setItem(
          LOCAL_STORAGE.xLandingFingerprintVisitorId,
          JSON.stringify(visitorLandingData),
        );

        if (fingerprintVisitorId && fingerprintRequestId) {
          // eslint-disable-next-line no-param-reassign
          postData.deviceDetails = {
            ...postData.deviceDetails,
            landingFingerprintVisitorId: {
              visitorId: fingerprintVisitorId,
              visitorSource: VISITOR_SOURCE.landing,
              requestId: fingerprintRequestId,
            },
          };
        }
      }

      setIsFingerprintDataLoading(false);

      onSuccess(postData);
    }
  };

  callNative({
    operation: NativeOperations.RETRIEVE_VISITOR_ID,
    callBackFoo: handleVisitorId,
    parameters: {},
  });
};

export const getInstalledApps = () => {
  const handleGetInstalledApps = (res: NativeGetInstalledAppsResponse) => {
    if (res?.installedApps) {
      store.dispatch(setInstalledApps(res.installedApps));
    }
  };

  callNative({
    operation: NativeOperations.RETRIEVE_INSTALLED_APPS,
    callBackFoo: handleGetInstalledApps,
    parameters: {},
  });
};

export const fingerprintTrustSdk = () => {
  const fingerprintTrustDecision = sessionStorage.getItem(LOCAL_STORAGE.fingerprintTrustDecision);

  return new Promise((resolve, reject) => {
    if (isWebContext && !fingerprintTrustDecision) {
      // eslint-disable-next-line no-underscore-dangle
      window._fmOpt = {
        partner: 'Monetech_mx',
        success(data: any) {
          sessionStorage.setItem(
            LOCAL_STORAGE.fingerprintTrustDecision,
            JSON.stringify({ visitorSource: 'pwa', requestId: data }),
          );
          resolve(data);
        },
      };

      const fm = document.createElement('script');

      fm.type = 'text/javascript';
      fm.async = true;
      fm.src =
        'https://static.trustdecision.com/tdfp/sg/d0665fb624b7e7662a15e3bc8e053d68/fm.js' +
        `?t=${(new Date().getTime() / 3600000).toFixed(0)}`;

      // fm.onload = () => {};

      fm.onerror = () => {
        reject(new Error('Failed to load the script'));
      };

      const s = document.getElementsByTagName('script')[0];

      s?.parentNode?.insertBefore(fm, s);
    }

    if (!isWebContext && !fingerprintTrustDecision) {
      const getBlackBox = (res: NativeTrustSdk) => {
        if (res?.status === ResStatusEnum.OK) {
          sessionStorage.setItem(
            LOCAL_STORAGE.fingerprintTrustDecision,
            JSON.stringify({
              visitorSource: VISITOR_SOURCE.mobApp,
              requestId: res?.blackbox,
            }),
          );
        }
        if (res?.status === ResStatusEnum.OK && res?.deepLinkUrl?.includes('blackbox')) {
          const urlParams = new URLSearchParams(res?.deepLinkUrl.split('?')[1] || '');

          const blackbox = urlParams.get('blackbox');

          sessionStorage.setItem(
            LOCAL_STORAGE.landingFingerprintTrustDecision,
            JSON.stringify({
              visitorSource: VISITOR_SOURCE.landing,
              requestId: blackbox,
            }),
          );
        }

        if (res?.status === ResStatusEnum.ERROR) {
          reject(new Error('blackbox error'));
        }
        resolve(res);
      };

      callNative({
        operation: NativeOperations.RETRIEVE_BLACKBOX,
        callBackFoo: getBlackBox,
        parameters: {},
      });
    }
    resolve('');
  });
};
